import { WebhookPayload, WebhookResponse, EmailAgentConfig } from '../types/webhook';

// Environment-aware configuration
const getWebhookUrl = () => {
  const env = import.meta.env.VITE_ENVIRONMENT || 'development';
  const customUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
  
  if (customUrl) {
    return customUrl;
  }
  
  // Default URLs by environment
  switch (env) {
    case 'production':
      return 'https://n8n.yourdomain.com/webhook/contact-form';
    case 'staging':
      return 'https://n8n-staging.yourdomain.com/webhook/contact-form';
    default:
      return 'http://localhost:5678/webhook-test/contact-form';
  }
};

const DEFAULT_CONFIG: EmailAgentConfig = {
  webhookUrl: getWebhookUrl(),
  timeout: import.meta.env.VITE_ENVIRONMENT === 'production' ? 15000 : 10000,
  retries: 3
};

/**
 * Submit form data to N8N Email Agent webhook
 */
export const submitToEmailAgent = async (
  formData: Omit<WebhookPayload, 'submittedAt' | 'userAgent' | 'source'>,
  config: Partial<EmailAgentConfig> = {}
): Promise<WebhookResponse> => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  const payload: WebhookPayload = {
    ...formData,
    submittedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    source: 'contact-form'
  };

  let lastError: Error | null = null;

  // Retry logic
  for (let attempt = 1; attempt <= finalConfig.retries; attempt++) {
    try {
      console.log(`📤 Sending to Email Agent (attempt ${attempt}/${finalConfig.retries}):`, payload);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), finalConfig.timeout);

      const response = await fetch(finalConfig.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'OneAI-ContactForm/1.0'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: WebhookResponse = await response.json();
      
      console.log('✅ Email Agent response:', result);
      
      return {
        success: true,
        message: result.message || 'Email agent activated successfully',
        workflowId: result.workflowId,
        executionId: result.executionId
      };

    } catch (error) {
      lastError = error as Error;
      console.warn(`⚠️ Email Agent attempt ${attempt} failed:`, error);

      // Don't retry on certain errors
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - Email agent is taking too long to respond');
        }
        if (error.message.includes('404')) {
          throw new Error('Email agent webhook not found - Please check N8N configuration');
        }
        if (error.message.includes('ECONNREFUSED')) {
          throw new Error('Cannot connect to Email agent - Is N8N running?');
        }
      }

      // Wait before retry (exponential backoff)
      if (attempt < finalConfig.retries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed
  throw new Error(
    lastError?.message || 'Failed to submit to Email agent after multiple attempts'
  );
};

/**
 * Test if N8N Email Agent is available
 */
export const testEmailAgentConnection = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 5000);

    const response = await fetch('http://localhost:5678/healthz', {
      method: 'GET',
      signal: controller.signal
    });

    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get Email Agent status information
 */
export const getEmailAgentStatus = async (): Promise<{
  available: boolean;
  webhookUrl: string;
  lastChecked: string;
}> => {
  const available = await testEmailAgentConnection();
  
  return {
    available,
    webhookUrl: DEFAULT_CONFIG.webhookUrl,
    lastChecked: new Date().toISOString()
  };
};
